<template>
    <form-group
        :group-classes="isFormField ? (horizontal ? 'mb-3' : '') : '!mb-0'"
        :horizontal="isFormField && horizontal"
        :inline="!isFormField"
        :label="isFormField ? label : ''"
        :label-classes="[{ required }]"
    >
        <Popover v-model:open="isOpen">
            <PopoverAnchor>
                <PopoverTrigger
                    :class="[
                        {
                            'c-show': isOpen,
                            'is-invalid': !!invalidFeedback,
                            disabled: isDisabled,
                            'min-h-12 w-100 !rounded-xl border bg-white px-2 py-3': isFormField,
                            '!border-danger': !!invalidFeedback && isFormField,
                        },
                        isFormField ? '' : 'multi-select',
                        classes,
                    ]"
                    :disabled="isDisabled"
                    class="focus-visible:outline-none"
                >
                    <slot :disabled="disabled" :placeholder="placeholder" name="toggler-content">
                        <div class="!flex items-center justify-between gap-2">
                            <slot :placeholder="placeholder" :selectedItems="selectedOptions" name="selectedValue">
                                <template v-if="isFormField">
                                    <div class="min-w-0 flex-shrink truncate text-start leading-4">
                                        <span v-if="selectedOptions.length">{{ displayValue }}</span>
                                        <span v-else class="text-muted">{{ placeholder }}</span>
                                    </div>
                                </template>
                                <span v-else class="truncate">{{ label }}</span>
                            </slot>
                            <div class="flex items-center gap-2">
                                <span
                                    v-if="selected.length > 0"
                                    class="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-white"
                                >
                                    {{ selected.length }}
                                </span>
                                <template v-if="isFormField">
                                    <spinner v-if="isProcessing" class="h-4 w-4" />
                                    <ChevronsUpDown
                                        v-else
                                        :class="{ 'text-danger': !!invalidFeedback && !isOpen }"
                                        class="h-4 w-4 shrink-0 opacity-75"
                                    />
                                </template>
                            </div>
                        </div>
                    </slot>
                </PopoverTrigger>
            </PopoverAnchor>
            <PopoverContent
                :align-flip="true"
                :disable-popover-portal="!teleportPopover"
                :prioritize-position="true"
                align="start"
                class="popover-content p-0"
            >
                <Command :highlight-on-hover="false" selection-behavior="manual">
                    <CommandInput v-if="searchEnabled" v-model="searchString" :placeholder="trans('general.placeholder.search')" />
                    <CommandList>
                        <CommandEmpty class="p-2 text-start text-base">{{ trans('general.selection.options.empty') }}</CommandEmpty>
                        <CommandGroup v-if="isSelectAllButtonVisible" class="flex flex-col gap-[1px]">
                            <CommandItem
                                class="flex cursor-pointer items-center gap-2 rounded-lg p-2 text-base! hover:bg-primary! hover:text-white data-[highlighted]:bg-transparent! data-[highlighted]:text-inherit! data-[highlighted]:hover:text-white!"
                                @select="toggleAll"
                            >
                                <span class="flex h-4 w-4 shrink-0 items-center justify-center text-primary">
                                    <Check v-if="isAllSelected" class="h-3.5 w-3.5" />
                                    <Minus v-else-if="isSomeSelected" class="h-3.5 w-3.5" />
                                </span>
                                <span class="text-primary">{{ trans('general.action.select_all') }}</span>
                            </CommandItem>
                        </CommandGroup>
                        <CommandGroup v-for="group in groupedOptions" :key="group.group" :heading="group.group" class="flex flex-col">
                            <template v-for="option in group.data" :key="option.value">
                                <CommandItem
                                    v-if="isOptionVisible(option)"
                                    :value="option.value"
                                    as="div"
                                    class="flex cursor-pointer items-center gap-2 rounded-lg p-2 text-base! hover:bg-primary! hover:text-white data-[highlighted]:bg-transparent! data-[highlighted]:text-inherit! data-[highlighted]:hover:bg-primary! data-[highlighted]:hover:text-white!"
                                    @select="toggleOption(option.value)"
                                >
                                    <span class="flex h-4 w-4 shrink-0 items-center justify-center">
                                        <Icon v-if="isSelected(option.value)" class="h-3.5 w-3.5" icon="cil-check-circle" />
                                    </span>
                                    <span class="min-w-0 flex-1">
                                        <slot :option="option" :show="isOpen" name="label">
                                            <span class="truncate">{{ option.label }}</span>
                                        </slot>
                                    </span>
                                </CommandItem>
                            </template>
                        </CommandGroup>
                    </CommandList>
                </Command>
                <div v-if="$slots.footer || $slots.actions || isClearButtonVisible" :class="footerClasses" class="border-t p-1">
                    <slot v-if="$slots.footer" :hide-element="hideElement" :show="isOpen" name="footer" />
                    <slot :deselect-all="deselectAll" name="actions">
                        <button
                            v-if="isClearButtonVisible"
                            :class="isClearButtonDisabled ? 'text-gray-body' : 'text-primary hover:underline'"
                            :disabled="isClearButtonDisabled"
                            class="w-full rounded-b-lg px-2 py-1 text-start text-sm hover:text-primary"
                            type="button"
                            @click="deselectAll"
                        >
                            {{ trans('general.action.clear') }}
                        </button>
                    </slot>
                </div>
            </PopoverContent>
        </Popover>
        <div v-if="!!invalidFeedback" class="invalid-feedback d-block" v-text="invalidFeedback" />
    </form-group>
</template>
<script setup>
import FormGroup from '@/Components/FormGroup.vue';
import { Check, ChevronsUpDown, Minus } from '@lucide/vue';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/Components/ui/command';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/Components/ui/popover';
import { computed, ref, watch } from 'vue';
import { trans } from '@/mixins/i18n.js';
import Spinner from '@/Components/Spinner.vue';
import Icon from '@/Components/Icon.vue';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
    label: String,
    name: String,
    options: {
        type: Array,
        default: () => [],
    },
    modelValue: {
        type: [String, Array],
        default: () => [],
    },
    placeholder: {
        type: String,
        default: () => trans('general.form.select.placeholder'),
    },
    isFormField: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    showSelectAllButton: {
        type: Boolean,
        default: true,
    },
    default: {
        type: Array,
        default: () => [],
    },
    showSearchbar: {
        type: Boolean,
        default: false,
    },
    isProcessing: {
        type: Boolean,
        default: false,
    },
    invalidFeedback: String,
    horizontal: {
        type: Boolean,
        default: true,
    },
    required: {
        type: Boolean,
        default: false,
    },
    footerClasses: [String, Array],
    classes: [String, Array],
    // Render the options popover in a body-level portal instead of inline.
    // Needed when an ancestor establishes a containing block (e.g. the
    // dashboard filter bar's `container-type: inline-size`), which otherwise
    // throws off the inline popover's anchored position.
    teleportPopover: {
        type: Boolean,
        default: false,
    },
});

const isOpen = ref(false);
const selected = ref([]);
const searchString = ref('');

const groupedOptions = computed(() => {
    if (Object.hasOwn(props.options?.[0] ?? {}, 'data')) {
        return props.options;
    }
    return [{ group: null, data: props.options || [] }];
});

const flatOptions = computed(() => {
    return groupedOptions.value.flatMap((group) => group.data);
});

const availableOptionsCount = computed(() => flatOptions.value.length);
const isAllSelected = computed(() => selected.value.length === availableOptionsCount.value && availableOptionsCount.value > 0);
const isSomeSelected = computed(() => selected.value.length > 0 && selected.value.length < availableOptionsCount.value);

const selectedOptions = computed(() => {
    const values = Array.isArray(props.modelValue) ? props.modelValue : [];
    return values.map((value) => flatOptions.value.find((option) => option.value === value)).filter((option) => option);
});

const displayValue = computed(() => {
    return selectedOptions.value.map((item) => item.label).join(', ');
});

const searchEnabled = computed(() => props.showSearchbar || availableOptionsCount.value > 5);
const isDisabled = computed(() => props.disabled);
const isClearButtonVisible = computed(() => availableOptionsCount.value > 2);
const isClearButtonDisabled = computed(() => selected.value.length === 0);
const isSelectAllButtonVisible = computed(() => availableOptionsCount.value > 2 && props.showSelectAllButton);

const isSelected = (value) => selected.value.includes(value);

const isOptionVisible = (option) => {
    if (!searchString.value) {
        return true;
    }
    return (option.searchableContent || option.label)?.toLowerCase().includes(searchString.value.toLowerCase());
};

const toggleOption = (value) => {
    const index = selected.value.indexOf(value);
    if (index === -1) {
        selected.value = [...selected.value, value];
    } else {
        selected.value = selected.value.filter((v) => v !== value);
    }
    emit('update:modelValue', selected.value);
};

const toggleAll = () => {
    if (isAllSelected.value) {
        selected.value = [];
    } else {
        selected.value = flatOptions.value.map((option) => option.value);
    }
    emit('update:modelValue', selected.value);
};

const deselectAll = () => {
    selected.value = [...props.default];
    searchString.value = '';
    emit('update:modelValue', selected.value);
    hideElement();
};

const hideElement = () => {
    isOpen.value = false;
};

watch(
    () => props.modelValue,
    (value) => {
        selected.value = Array.isArray(value) ? value : [];
    },
    { immediate: true },
);

watch(isOpen, (value) => {
    if (!value) {
        searchString.value = '';
    }
});
</script>
<style>
.popover-content {
    width: max-content;
    min-width: var(--reka-popover-trigger-width);
    max-width: var(--reka-popover-content-available-width, 100vw);
    max-height: var(--reka-popover-content-available-height);
}
</style>
